import type { Course, HoleScore, Player, Round, ScoreFormat } from './types';

/** Strokes a player receives on a hole under the standard allocation. */
export function strokesReceived(handicap: number, strokeIndex: number, holes = 18): number {
  const hc = Math.round(handicap);
  if (hc <= 0) {
    // Plus handicaps give shots back, starting at the easiest hole.
    const give = Math.abs(hc);
    return strokeIndex > holes - give ? -1 : 0;
  }
  const base = Math.floor(hc / holes);
  const extra = hc % holes;
  return base + (strokeIndex <= extra ? 1 : 0);
}

export function stablefordPoints(strokes: number, par: number, received: number): number {
  const net = strokes - received;
  const diff = par - net;
  return Math.max(0, diff + 2);
}

export interface PlayerTotals {
  playerId: string;
  holesPlayed: number;
  gross: number;
  net: number;
  points: number;
  toPar: number;
  putts: number;
  fairways: { hit: number; of: number };
  gir: { hit: number; of: number };
}

export function totalsFor(round: Round, course: Course, player: Player): PlayerTotals {
  const scores = round.scores[player.id] ?? [];
  const t: PlayerTotals = {
    playerId: player.id,
    holesPlayed: 0,
    gross: 0,
    net: 0,
    points: 0,
    toPar: 0,
    putts: 0,
    fairways: { hit: 0, of: 0 },
    gir: { hit: 0, of: 0 },
  };

  for (const s of scores) {
    if (s.strokes == null) continue;
    const hole = course.holes.find((h) => h.number === s.hole);
    if (!hole) continue;
    const recv = strokesReceived(player.handicap, hole.strokeIndex);
    t.holesPlayed++;
    t.gross += s.strokes;
    t.net += s.strokes - recv;
    t.points += stablefordPoints(s.strokes, hole.par, recv);
    t.toPar += s.strokes - hole.par;
    t.putts += s.putts ?? 0;
    if (hole.par > 3) {
      t.fairways.of++;
      if (s.fairwayHit) t.fairways.hit++;
    }
    t.gir.of++;
    if (s.gir) t.gir.hit++;
  }
  return t;
}

/** Match play status from player A's point of view: +2 = 2 up. */
export function matchStatus(round: Round, course: Course, a: Player, b: Player) {
  let up = 0;
  let played = 0;
  for (const hole of course.holes) {
    const sa = round.scores[a.id]?.find((s) => s.hole === hole.number);
    const sb = round.scores[b.id]?.find((s) => s.hole === hole.number);
    if (sa?.strokes == null || sb?.strokes == null) continue;
    played++;
    const na = sa.strokes - strokesReceived(a.handicap, hole.strokeIndex);
    const nb = sb.strokes - strokesReceived(b.handicap, hole.strokeIndex);
    if (na < nb) up++;
    else if (nb < na) up--;
  }
  const remaining = course.holes.length - played;
  const dormie = Math.abs(up) === remaining && remaining > 0;
  const decided = Math.abs(up) > remaining;
  return { up, played, remaining, dormie, decided };
}

export function scoreLabel(strokes: number, par: number): string {
  const d = strokes - par;
  if (strokes === 1) return 'Ace';
  if (d <= -3) return 'Albatross';
  if (d === -2) return 'Eagle';
  if (d === -1) return 'Birdie';
  if (d === 0) return 'Par';
  if (d === 1) return 'Bogey';
  if (d === 2) return 'Double';
  return `+${d}`;
}

export function emptyCard(course: Course): HoleScore[] {
  return course.holes.map((h) => ({
    hole: h.number,
    strokes: null,
    putts: null,
    penalties: 0,
    fairwayHit: null,
    gir: null,
  }));
}

export function newRound(opts: {
  course: Course;
  buggyId: string;
  players: Player[];
  format: ScoreFormat;
}): Round {
  const scores: Record<string, HoleScore[]> = {};
  for (const p of opts.players) scores[p.id] = emptyCard(opts.course);
  return {
    id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    courseId: opts.course.id,
    courseName: opts.course.name,
    buggyId: opts.buggyId,
    format: opts.format,
    startedAt: Date.now(),
    finishedAt: null,
    players: opts.players,
    scores,
    rev: 0,
  };
}

/**
 * Pace of play. Field standard is ~13 min a hole for a fourball in a buggy;
 * the pro shop cares about the delta, not the absolute.
 */
export function paceStatus(round: Round, holesComplete: number, minutesPerHole = 13) {
  const elapsedMin = (Date.now() - round.startedAt) / 60_000;
  const expected = holesComplete * minutesPerHole;
  const deltaMin = Math.round(elapsedMin - expected);
  const state: 'ahead' | 'on' | 'behind' = deltaMin < -5 ? 'ahead' : deltaMin > 8 ? 'behind' : 'on';
  return { elapsedMin: Math.round(elapsedMin), deltaMin, state };
}
